import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import API_BASE_URL from '../config';
import Spectrogram from './Spectrogram';
import AudioVisualization from './AudioVisualization';
import './AudioPlayer.css';

const AudioPlayer = ({ track, isPlaying, onPlayPause, onTrackEnd, tracks = [], onTrackSelect, fixed = false, onNextTrack, onPreviousTrack }) => {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [volume, setVolume] = useState(1); // Сохраняем для возможного использования в будущем
  const [isRewinding, setIsRewinding] = useState(false);
  const [isFastForwarding, setIsFastForwarding] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [filterMode, setFilterMode] = useState('underground'); // 'commercial' или 'underground', по умолчанию 'underground'
  const [isMobile, setIsMobile] = useState(false); // Флаг для определения мобильного устройства
  const rewindIntervalRef = useRef(null);
  const fastForwardIntervalRef = useRef(null);
  const isStoppedRef = useRef(false); // Флаг для отслеживания явной остановки
  const isFilterChangingRef = useRef(false); // Флаг для отслеживания смены фильтра
  const clickSoundRefs = useRef({
    play: null,
    stop: null,
    click: null
  });

  // Определяем мобильное устройство
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Инициализация звуковых файлов для кнопок
  useEffect(() => {
    // Создаем отдельные Audio элементы для каждого звука
    // Это позволяет воспроизводить несколько звуков одновременно без конфликтов
    const playSound = new Audio('/sound/play.mp3');
    const stopSound = new Audio('/sound/stop.mp3');
    const clickSound = new Audio('/sound/click.mp3');
    
    // Сохраняем ссылки в ref
    clickSoundRefs.current.play = playSound;
    clickSoundRefs.current.stop = stopSound;
    clickSoundRefs.current.click = clickSound;
    
    // Предзагружаем звуки для низкой задержки
    const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    [playSound, stopSound, clickSound].forEach(audio => {
      if (audio) {
        // Senior Fullstack: Оптимизация для мобильных - тише звуки на мобильных
        audio.volume = isMobileDevice ? 0.1 : 0.3;
        audio.preload = 'auto';
      }
    });

    // Очистка при размонтировании
    return () => {
      // Используем сохраненные ссылки из замыкания
      if (playSound) {
        playSound.pause();
        playSound.src = '';
      }
      if (stopSound) {
        stopSound.pause();
        stopSound.src = '';
      }
      if (clickSound) {
        clickSound.pause();
        clickSound.src = '';
      }
    };
  }, []);

  // Функция для воспроизведения звука клика кнопки
  const playClickSound = (type = 'click') => {
    try {
      const soundFile = clickSoundRefs.current[type];
      if (!soundFile) return;

      // Клонируем аудио элемент для одновременного воспроизведения нескольких кликов
      const audioClone = soundFile.cloneNode();
      audioClone.volume = 0.3;
      
      // Сбрасываем время для мгновенного воспроизведения
      audioClone.currentTime = 0;
      
      // Воспроизводим звук
      audioClone.play().catch(err => {
        // Игнорируем ошибки (например, если пользователь не взаимодействовал со страницей)
        console.debug('Click sound play error (ignored):', err);
      });
    } catch (error) {
      // Игнорируем ошибки воспроизведения звука
      console.debug('Click sound error (ignored):', error);
    }
  };

  // Обработчик изменения громкости из эквалайзера
  // Просто обновляем состояние, эквалайзер сам управляет громкостью
  const handleVolumeChange = useCallback((newVolume) => {
    setVolume(newVolume);
  }, []);

  // Загрузка трека
  useEffect(() => {
    if (!track || !audioRef.current) return;

    // Проверяем, соответствует ли трек текущему фильтру
    if (track.project !== filterMode) {
      // Трек не соответствует фильтру - не загружаем его
      // Это нормальное поведение при смене фильтра, поэтому используем debug вместо warn
      console.debug('Track does not match current filter mode, skipping load');
      return;
    }

    const audio = audioRef.current;
    // Используем API_BASE_URL для формирования полного URL трека
    const audioUrl = track.url.startsWith('http') 
      ? track.url 
      : `${API_BASE_URL}${track.url.startsWith('/') ? track.url : '/' + track.url}`;
    
    // Проверяем, не загружаем ли мы тот же трек (избегаем ненужной перезагрузки)
    const currentSrc = audio.src;
    const normalizedCurrentSrc = currentSrc.replace(window.location.origin, '');
    const normalizedNewSrc = audioUrl;
    
    if (normalizedCurrentSrc === normalizedNewSrc || currentSrc === audioUrl) {
      // Трек уже загружен, не перезагружаем
      return;
    }
    
    // Останавливаем все интервалы перемотки при переключении трека
    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
      rewindIntervalRef.current = null;
    }
    if (fastForwardIntervalRef.current) {
      clearInterval(fastForwardIntervalRef.current);
      fastForwardIntervalRef.current = null;
    }
    
    // Сбрасываем состояния перемотки
    setIsRewinding(false);
    setIsFastForwarding(false);
    
    // Сбрасываем флаг остановки при загрузке нового трека (если трек меняется, это не явная остановка)
    // Но сохраняем флаг, если трек не меняется (т.е. это перезагрузка того же трека)
    if (normalizedCurrentSrc !== normalizedNewSrc && currentSrc !== audioUrl) {
      isStoppedRef.current = false;
    }
    
    // Сохраняем текущее состояние воспроизведения перед загрузкой нового трека
    const wasPlaying = !audio.paused && !isStoppedRef.current;
    
    // Останавливаем предыдущее воспроизведение только если трек действительно изменился
    audio.pause();
    audio.src = '';
    
    // Устанавливаем новый src
    console.log('Loading audio:', audioUrl);
    console.log('Track URL:', track.url);
    console.log('API_BASE_URL:', API_BASE_URL);
    audio.src = audioUrl;
    
    // Загружаем аудио
    audio.load();

    // Обработчик готовности к воспроизведению
    const handleCanPlay = () => {
      console.log('Audio ready to play');
      // Обновляем duration при готовности
      if (audio.duration) {
        setDuration(audio.duration);
      }
      // Автоматически воспроизводим если isPlaying = true или было воспроизведение до переключения
      // НО только если не была явная остановка
      if (!isStoppedRef.current && (isPlaying || wasPlaying)) {
        audio.play().catch(err => {
          console.error('Error auto-playing:', err);
        });
      }
    };

    // Обработчик загрузки метаданных
    const handleLoadedMetadata = () => {
      if (audio.duration) {
        setDuration(audio.duration);
      }
    };

    // Обработчик ошибок загрузки
    const handleError = (e) => {
      console.error('Audio loading error:', e);
      console.error('Audio src:', audio.src);
      console.error('Audio error code:', audio.error?.code);
      console.error('Audio error message:', audio.error?.message);
      console.error('Audio networkState:', audio.networkState);
      console.error('Audio readyState:', audio.readyState);
    };

    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);

    // Очистка
    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      // Очищаем интервалы при размонтировании
      if (rewindIntervalRef.current) {
        clearInterval(rewindIntervalRef.current);
        rewindIntervalRef.current = null;
      }
      if (fastForwardIntervalRef.current) {
        clearInterval(fastForwardIntervalRef.current);
        fastForwardIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, filterMode]); // Добавлен filterMode для проверки соответствия фильтру

  // Управление воспроизведением/паузой
  useEffect(() => {
    if (!audioRef.current || !track) return;

    const audio = audioRef.current;
    
    // Если происходит смена фильтра, не запускаем воспроизведение и останавливаем, если играет
    if (isFilterChangingRef.current) {
      if (!audio.paused) {
        audio.pause();
        onPlayPause(false);
      }
      return;
    }
    
    // Проверяем, соответствует ли трек текущему фильтру
    if (track.project !== filterMode) {
      // Трек не соответствует фильтру - не воспроизводим и останавливаем, если играет
      if (!audio.paused) {
        audio.pause();
        onPlayPause(false);
      }
      return;
    }
    
    // Сбрасываем флаг остановки при начале воспроизведения
    if (isPlaying) {
      isStoppedRef.current = false;
    }
    
    // Проверяем, не пытаемся ли мы изменить состояние, которое уже установлено
    const isCurrentlyPlaying = !audio.paused;
    if (isPlaying === isCurrentlyPlaying) {
      // Состояние уже соответствует, не нужно ничего делать
      return;
    }
    
    const controlPlayback = async () => {
      try {
        if (isPlaying) {
          // НЕ воспроизводим, если была явная остановка
          if (isStoppedRef.current) {
            return;
          }
          
          // Проверяем готовность перед воспроизведением
          if (audio.readyState >= 2) {
            await audio.play();
          } else {
            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                audio.removeEventListener('canplay', handleCanPlay);
                reject(new Error('Timeout waiting for audio'));
              }, 10000);

              const handleCanPlay = () => {
                clearTimeout(timeout);
                audio.removeEventListener('canplay', handleCanPlay);
                resolve();
              };

              audio.addEventListener('canplay', handleCanPlay);
              
              if (audio.readyState >= 2) {
                clearTimeout(timeout);
                audio.removeEventListener('canplay', handleCanPlay);
                resolve();
              }
            });
            
            await audio.play();
          }
        } else {
          audio.pause();
        }
      } catch (error) {
        console.error('Error controlling playback:', error);
        if (error.name !== 'AbortError' && error.message !== 'The play() request was interrupted') {
          console.warn('Playback error:', error.message);
        }
      }
    };

    controlPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, filterMode]); // Добавлен filterMode для проверки соответствия фильтру

  const lastUpdateTimeRef = useRef(0);
  const handleTimeUpdate = () => {
    if (audioRef.current && !isDragging) {
      const now = Date.now();
      // Senior Fullstack: Оптимизация для мобильных - более редкие обновления
      const throttleInterval = isMobile ? 200 : 100; // 200мс на мобильных, 100мс на десктопе
      if (now - lastUpdateTimeRef.current >= throttleInterval) {
        setCurrentTime(audioRef.current.currentTime);
        lastUpdateTimeRef.current = now;
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleEnded = () => {
    setCurrentTime(0);
    setDuration(0);
    
    // КРИТИЧНО: Используем локальную логику переключения, которая учитывает только отфильтрованный список
    // Проверяем, есть ли следующий трек в отфильтрованном списке
    const filteredTracks = displayTracks;
    if (filteredTracks.length === 0 || !track) {
      // Нет треков в списке или нет текущего трека - просто останавливаем
      onPlayPause(false);
      return;
    }
    
    // КРИТИЧНО: Проверяем, что текущий трек соответствует текущему фильтру
    if (track.project !== filterMode) {
      // Трек не соответствует фильтру - останавливаем воспроизведение
      onPlayPause(false);
      return;
    }
    
    const currentIndex = filteredTracks.findIndex(t => t.id === track.id);
    if (currentIndex === -1 || currentIndex === filteredTracks.length - 1) {
      // Текущий трек не в списке или это последний трек - останавливаем воспроизведение
      onPlayPause(false);
      return;
    }
    
    // Есть следующий трек в отфильтрованном списке - переключаемся на него
    const nextTrack = filteredTracks[currentIndex + 1];
    
    // КРИТИЧНО: Проверяем, что следующий трек соответствует текущему фильтру
    if (!nextTrack || nextTrack.project !== filterMode) {
      // Следующий трек не соответствует фильтру - останавливаем воспроизведение
      onPlayPause(false);
      return;
    }
    
    if (onTrackSelect) {
      onTrackSelect(nextTrack);
      // Автоматически начинаем воспроизведение следующего трека
      setTimeout(() => {
        onPlayPause(true);
      }, 100);
    } else {
      // Если нет onTrackSelect, используем стандартный onTrackEnd
      onTrackEnd();
    }
  };

  // Обработка изменения позиции прогресс-бара
  const handleProgressChange = (clientX, target) => {
    if (!audioRef.current || !duration) return;
    
    const rect = target.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  const handleProgressMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    handleProgressChange(e.clientX, e.currentTarget);
  };

  const handleProgressMouseUp = (e) => {
    setIsDragging(false);
    if (audioRef.current && duration) {
      handleProgressChange(e.clientX, e.currentTarget);
    }
  };

  const handleProgressTouchStart = (e) => {
    // Не вызываем preventDefault в пассивном обработчике
    // Вместо этого используем CSS touch-action
    setIsDragging(true);
    const touch = e.touches[0];
    handleProgressChange(touch.clientX, e.currentTarget);
  };

  const handleProgressTouchMove = (e) => {
    if (isDragging) {
      // Не вызываем preventDefault в пассивном обработчике
      const touch = e.touches[0];
      handleProgressChange(touch.clientX, e.currentTarget);
    }
  };

  const handleProgressTouchEnd = (e) => {
    setIsDragging(false);
  };

  const handleStop = useCallback(() => {
    // Устанавливаем флаг остановки ПЕРВЫМ делом
    isStoppedRef.current = true;
    
    // Воспроизводим звук остановки
    playClickSound('stop');
    
    // 1. Останавливаем все интервалы перемотки (критично - делаем первым)
    const rewindInterval = rewindIntervalRef.current;
    const fastForwardInterval = fastForwardIntervalRef.current;
    
    if (rewindInterval) {
      clearInterval(rewindInterval);
      rewindIntervalRef.current = null;
    }
    if (fastForwardInterval) {
      clearInterval(fastForwardInterval);
      fastForwardIntervalRef.current = null;
    }
    
    // 2. Сбрасываем состояния перемотки
    setIsRewinding(false);
    setIsFastForwarding(false);
    
    // 3. Останавливаем воспроизведение основного трека
    const stopAudio = () => {
      const audio = audioRef.current;
      if (audio) {
        try {
          audio.pause();
          audio.currentTime = 0;
          setCurrentTime(0);
        } catch (error) {
          console.debug('Error stopping audio:', error);
        }
      }
    };
    
    // Пытаемся остановить сразу
    stopAudio();
    
    // Если audioRef еще не готов, пробуем через небольшую задержку
    setTimeout(stopAudio, 10);
    setTimeout(stopAudio, 50);
    setTimeout(stopAudio, 100);
    
    // 4. Обновляем состояние воспроизведения (всегда, независимо от audioRef)
    // Это критично - должно выполняться всегда
    onPlayPause(false);
  }, [onPlayPause]);

  // Очистка интервалов при размонтировании
  useEffect(() => {
    return () => {
      if (rewindIntervalRef.current) {
        clearInterval(rewindIntervalRef.current);
      }
      if (fastForwardIntervalRef.current) {
        clearInterval(fastForwardIntervalRef.current);
      }
    };
  }, []);

  // Остановка перемотки при отсутствии трека
  useEffect(() => {
    if (!track) {
      setIsRewinding(false);
      setIsFastForwarding(false);
      if (rewindIntervalRef.current) {
        clearInterval(rewindIntervalRef.current);
        rewindIntervalRef.current = null;
      }
      if (fastForwardIntervalRef.current) {
        clearInterval(fastForwardIntervalRef.current);
        fastForwardIntervalRef.current = null;
      }
    }
  }, [track]);

  const startRewind = () => {
    if (!audioRef.current || !track) return;
    
    // Очищаем предыдущий интервал если он существует
    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
      rewindIntervalRef.current = null;
    }
    
    playClickSound('click');
    
    setIsRewinding(true);
    const wasPlaying = isPlaying;
    
    // Останавливаем быструю перемотку вперед если она активна
    if (isFastForwarding) {
      stopFastForward();
    }
    
    // Паузим воспроизведение во время перемотки
    if (wasPlaying) {
      audioRef.current.pause();
    }
    
    // Непрерывная перемотка назад с интервалом
    rewindIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        const newTime = Math.max(0, audioRef.current.currentTime - 0.5); // Перемотка на 0.5 сек каждые 50мс = 10x скорость
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
        
        // Если достигли начала, останавливаем перемотку
        if (newTime === 0) {
          stopRewind();
        }
      }
    }, 50); // Обновление каждые 50мс для плавной перемотки
  };

  const stopRewind = () => {
    setIsRewinding(false);
    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
      rewindIntervalRef.current = null;
    }
    
    // НЕ возобновляем воспроизведение, если была явная остановка
    if (isStoppedRef.current) {
      return;
    }
    
    // Возобновляем воспроизведение если оно было до перемотки
    if (isPlaying && audioRef.current && !isStoppedRef.current) {
      audioRef.current.play().catch(err => {
        console.debug('Error resuming playback after rewind:', err);
      });
    }
  };

  const startFastForward = () => {
    if (!audioRef.current || !track) return;
    
    // Очищаем предыдущий интервал если он существует
    if (fastForwardIntervalRef.current) {
      clearInterval(fastForwardIntervalRef.current);
      fastForwardIntervalRef.current = null;
    }
    
    playClickSound('click');
    
    setIsFastForwarding(true);
    const wasPlaying = isPlaying;
    
    // Останавливаем перемотку назад если она активна
    if (isRewinding) {
      stopRewind();
    }
    
    // Паузим воспроизведение во время перемотки
    if (wasPlaying) {
      audioRef.current.pause();
    }
    
    // Непрерывная перемотка вперед с интервалом
    fastForwardIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        const currentTime = audioRef.current.currentTime;
        const duration = audioRef.current.duration || 0;
        const newTime = Math.min(duration, currentTime + 0.5); // Перемотка на 0.5 сек каждые 50мс = 10x скорость
        
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
        
        // Если достигли конца, останавливаем перемотку
        if (newTime >= duration) {
          stopFastForward();
        }
      }
    }, 50); // Обновление каждые 50мс для плавной перемотки
  };

  const stopFastForward = () => {
    setIsFastForwarding(false);
    if (fastForwardIntervalRef.current) {
      clearInterval(fastForwardIntervalRef.current);
      fastForwardIntervalRef.current = null;
    }
    
    // НЕ возобновляем воспроизведение, если была явная остановка
    if (isStoppedRef.current) {
      return;
    }
    
    // Возобновляем воспроизведение если оно было до перемотки
    if (isPlaying && audioRef.current && !isStoppedRef.current) {
      audioRef.current.play().catch(err => {
        console.debug('Error resuming playback after fast-forward:', err);
      });
    }
  };

  // Обработчики для одиночного нажатия (быстрая перемотка на 5 секунд)
  const handleRewindClick = () => {
    if (audioRef.current) {
      playClickSound('click');
      const newTime = Math.max(0, audioRef.current.currentTime - 5);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleFastForwardClick = () => {
    if (audioRef.current) {
      playClickSound('click');
      const duration = audioRef.current.duration || 0;
      const newTime = Math.min(duration, audioRef.current.currentTime + 5);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handlePreviousTrack = () => {
    if (!tracks || tracks.length === 0 || !track) return;
    playClickSound('click');
    // Сбрасываем флаг остановки при переключении трека
    isStoppedRef.current = false;
    
    // КРИТИЧНО: Используем displayTracks (отфильтрованные треки) вместо всех tracks
    const filteredTracks = displayTracks;
    if (filteredTracks.length === 0) return;
    
    // КРИТИЧНО: Проверяем, что текущий трек соответствует текущему фильтру
    if (track.project !== filterMode) {
      console.warn('Current track does not match filter mode, stopping playback');
      onPlayPause(false);
      return;
    }
    
    const currentIndex = filteredTracks.findIndex(t => t.id === track.id);
    if (currentIndex === -1) {
      // Текущий трек не в отфильтрованном списке, выбираем последний трек из списка
      const lastTrack = filteredTracks[filteredTracks.length - 1];
      // КРИТИЧНО: Проверяем, что выбранный трек соответствует фильтру
      if (!lastTrack || lastTrack.project !== filterMode) {
        onPlayPause(false);
        return;
      }
      if (onPreviousTrack) {
        onPreviousTrack(lastTrack);
      } else if (onTrackSelect) {
        onTrackSelect(lastTrack);
        if (isPlaying) {
          setTimeout(() => {
            onPlayPause(true);
          }, 100);
        }
      }
      return;
    }
    
    // Если мы на первом треке, не переключаемся (убираем циклическое переключение)
    if (currentIndex === 0) {
      return;
    }
    
    const previousIndex = currentIndex - 1;
    const previousTrack = filteredTracks[previousIndex];
    
    // КРИТИЧНО: Проверяем, что предыдущий трек соответствует фильтру
    if (!previousTrack || previousTrack.project !== filterMode) {
      console.warn('Previous track does not match filter mode');
      onPlayPause(false);
      return;
    }
    
    if (onPreviousTrack) {
      onPreviousTrack(previousTrack);
    } else if (onTrackSelect) {
      onTrackSelect(previousTrack);
      if (isPlaying) {
        // Автоматически начинаем воспроизведение следующего трека
        setTimeout(() => {
          onPlayPause(true);
        }, 100);
      }
    }
  };

  const handleNextTrack = () => {
    if (!tracks || tracks.length === 0 || !track) return;
    playClickSound('click');
    // Сбрасываем флаг остановки при переключении трека
    isStoppedRef.current = false;
    
    // КРИТИЧНО: Используем displayTracks (отфильтрованные треки) вместо всех tracks
    const filteredTracks = displayTracks;
    if (filteredTracks.length === 0) return;
    
    // КРИТИЧНО: Проверяем, что текущий трек соответствует текущему фильтру
    if (track.project !== filterMode) {
      console.warn('Current track does not match filter mode, stopping playback');
      onPlayPause(false);
      return;
    }
    
    const currentIndex = filteredTracks.findIndex(t => t.id === track.id);
    if (currentIndex === -1) {
      // Текущий трек не в отфильтрованном списке, выбираем первый трек из списка
      const firstTrack = filteredTracks[0];
      // КРИТИЧНО: Проверяем, что выбранный трек соответствует фильтру
      if (!firstTrack || firstTrack.project !== filterMode) {
        onPlayPause(false);
        return;
      }
      if (onNextTrack) {
        onNextTrack(firstTrack);
      } else if (onTrackSelect) {
        onTrackSelect(firstTrack);
        if (isPlaying) {
          setTimeout(() => {
            onPlayPause(true);
          }, 100);
        }
      }
      return;
    }
    
    // Если мы на последнем треке, не переключаемся (убираем циклическое переключение)
    if (currentIndex === filteredTracks.length - 1) {
      return;
    }
    
    const nextIndex = currentIndex + 1;
    const nextTrack = filteredTracks[nextIndex];
    
    // КРИТИЧНО: Проверяем, что следующий трек соответствует фильтру
    if (!nextTrack || nextTrack.project !== filterMode) {
      console.warn('Next track does not match filter mode');
      onPlayPause(false);
      return;
    }
    
    if (onNextTrack) {
      onNextTrack(nextTrack);
    } else if (onTrackSelect) {
      onTrackSelect(nextTrack);
      if (isPlaying) {
        // Автоматически начинаем воспроизведение следующего трека
        setTimeout(() => {
          onPlayPause(true);
        }, 100);
      }
    }
  };


  // Фильтруем треки по выбранному режиму
  const displayTracks = React.useMemo(() => {
    return (tracks || []).filter(t => t.project === filterMode);
  }, [tracks, filterMode]);

  // При изменении фильтра проверяем, соответствует ли текущий трек новому фильтру
  useEffect(() => {
    // Устанавливаем флаг ДО любых действий
    isFilterChangingRef.current = true;
    
    // Сразу останавливаем воспроизведение, если оно идет
    if (isPlaying) {
      onPlayPause(false);
    }
    
    // Останавливаем аудио напрямую, если оно играет
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
    
    if (!track) {
      // Если нет текущего трека, но есть треки в отфильтрованном списке, выбираем первый БЕЗ запуска воспроизведения
      if (displayTracks.length > 0 && onTrackSelect) {
        // Выбираем первый трек БЕЗ запуска воспроизведения (передаем false как второй параметр)
        onTrackSelect(displayTracks[0], false);
      }
      // Сбрасываем флаг после задержки и гарантируем остановку
      setTimeout(() => {
        onPlayPause(false);
        if (audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause();
        }
        // Увеличиваем время блокировки до 500мс
        setTimeout(() => {
          isFilterChangingRef.current = false;
        }, 400);
      }, 50);
      return;
    }
    
    // Проверяем, соответствует ли текущий трек выбранному фильтру
    if (track.project !== filterMode) {
      // Текущий трек не соответствует фильтру - останавливаем воспроизведение
      // Если есть треки в отфильтрованном списке, выбираем первый БЕЗ запуска воспроизведения
      if (displayTracks.length > 0 && onTrackSelect) {
        // Выбираем первый трек БЕЗ запуска воспроизведения (передаем false как второй параметр)
        onTrackSelect(displayTracks[0], false);
      } else if (onTrackSelect) {
        // Если нет треков в списке, сбрасываем выбор
        onTrackSelect(null, false);
      }
    }
    
    // Сбрасываем флаг после задержки и гарантируем остановку
    setTimeout(() => {
      onPlayPause(false);
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
      // Увеличиваем время блокировки до 500мс
      setTimeout(() => {
        isFilterChangingRef.current = false;
      }, 400);
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode]); // track и isPlaying намеренно исключены, чтобы избежать циклов

  return (
    <div className={`boombox-container ${fixed ? 'fixed' : ''}`}>
      <audio
        ref={audioRef}
        crossOrigin="anonymous"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      {/* Оболочка магнитофона в стиле 90-х */}
      <div className="boombox-body">
        {/* Верхняя панель с брендингом */}
        <div className="boombox-top-panel">
          <div className="boombox-brand">STEREO CASSETTE DECK</div>
          <div className="boombox-model-container">
            <div className="boombox-model">MODEL 1990</div>
            <div className="boombox-made-in">made in <span className="boombox-ussr">USSR</span></div>
          </div>
        </div>

        {/* Основная панель */}
        <div className="boombox-main-panel">
          {/* Левая часть - кассета */}
          <div className="boombox-left-section">
      <div className="cassette-tape-container">
        <div className="cassette-body">
          {/* Черный лейбл */}
          <div className="cassette-label-black">
            <div className="label-side-a">A</div>
            <div className="label-info">
              <div className="label-title">{track ? track.title.toUpperCase() : 'TRACK TITLE'}</div>
              <div className="label-artist">{track ? track.artist.toUpperCase() : 'ARTIST NAME'}</div>
            </div>
            <div className="label-meters">
              <div className="meter-bar"></div>
              <div className="meter-bar"></div>
              <div className="meter-bar"></div>
            </div>
          </div>

          {/* Красная область с катушками */}
          <div className="cassette-red-section">
            <div className="cassette-labels">
              <div className="label-in-out">
                <div className="label-in-out-item">
                  <span className="in-out-box"></span>
                  <span>IN</span>
                </div>
                <div className="label-in-out-item">
                  <span className="in-out-box"></span>
                  <span>OUT</span>
                </div>
              </div>
            </div>

            <div className="cassette-reels-container">
              <motion.div
                className={`tape-reel-static reel-left ${
                  isRewinding ? 'rewinding' : 
                  isFastForwarding ? 'fast-forwarding' : 
                  isPlaying && track ? 'active' : 'inactive'
                }`}
                animate={{
                  rotate: isRewinding ? -720 : // Быстрее при перемотке назад
                          isFastForwarding ? -720 : // Быстрее при перемотке вперед (одинаковая скорость)
                          isPlaying && track ? -360 : 0, // Против часовой стрелки при воспроизведении
                }}
                transition={{
                  duration: isRewinding ? 0.5 : // Быстрое вращение при перемотке назад
                           isFastForwarding ? 0.5 : // Быстрое вращение при перемотке вперед (одинаковая скорость)
                           2, // Нормальная скорость (одинаковая для обеих катушек)
                  repeat: (isRewinding || isFastForwarding || (isPlaying && track)) ? Infinity : 0,
                  ease: "linear"
                }}
              >
                <div className="reel-center-static"></div>
                <div className={`reel-tape-wound-left ${isRewinding ? 'rewinding' : ''}`}></div>
                <div className="reel-holes-static">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="hole-static"></div>
                  ))}
                </div>
              </motion.div>
              
              {/* Лента между катушками */}
              <div className={`tape-path ${
                isRewinding ? 'rewinding' : 
                isFastForwarding ? 'fast-forwarding' : 
                isPlaying && track ? 'playing' : ''
              }`}>
                <div className="tape-strip">
                  <div className={`tape-strip-pattern ${
                    isRewinding ? 'rewinding' : 
                    isFastForwarding ? 'fast-forwarding' : 
                    isPlaying && track ? 'moving' : ''
                  }`}></div>
                </div>
                <div className="tape-guide tape-guide-top"></div>
                <div className="tape-guide tape-guide-bottom"></div>
              </div>
              
              <motion.div
                className={`tape-reel-static reel-right ${
                  isRewinding ? 'rewinding' : 
                  isFastForwarding ? 'fast-forwarding' : 
                  isPlaying && track ? 'active' : 'inactive'
                }`}
                animate={{
                  rotate: isRewinding ? 720 : // Быстрее при перемотке назад (одинаковая скорость)
                          isFastForwarding ? 720 : // Быстрее при перемотке вперед (одинаковая скорость)
                          isPlaying && track ? -360 : 0, // Против часовой стрелки при воспроизведении (одинаковая скорость и направление)
                }}
                transition={{
                  duration: isRewinding ? 0.5 : // Быстрое вращение при перемотке назад (одинаковая скорость)
                           isFastForwarding ? 0.5 : // Быстрое вращение при перемотке вперед (одинаковая скорость)
                           2, // Нормальная скорость (одинаковая для обеих катушек)
                  repeat: (isRewinding || isFastForwarding || (isPlaying && track)) ? Infinity : 0,
                  ease: "linear"
                }}
              >
                <div className="reel-center-static"></div>
                <div className={`reel-tape-wound-right ${isFastForwarding ? 'fast-forwarding' : ''}`}></div>
                <div className="reel-holes-static">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="hole-static"></div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="cassette-c90">C90</div>
          </div>

          {/* Нижняя информация */}
          <div className="cassette-bottom-info">
            <div>TYPE I (NORMAL) POSITION</div>
            <div>NORMAL BIAS EQ 120 µs</div>
            <div>MADE IN JAPAN</div>
          </div>
          </div>
        </div>
      </div>

          {/* Правая часть - управление и визуализация */}
          <div className="boombox-right-section">
            {/* Аудио визуализация - скрыта на мобильных для оптимизации */}
            {!isMobile && <AudioVisualization audioRef={audioRef} isPlaying={isPlaying} />}

            {/* LED индикаторы */}
            <div className="boombox-leds">
              <div className={`led-indicator ${isPlaying && track ? 'led-on' : ''}`}>
                <span className="led-label">PLAY</span>
              </div>
              <div className={`led-indicator ${!isPlaying ? 'led-on' : ''}`}>
                <span className="led-label">READY</span>
              </div>
            </div>

            {/* Кнопки фильтрации режимов */}
            <div className="boombox-filter-buttons">
              <button
                className={`filter-button ${filterMode === 'commercial' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Предотвращаем множественные клики
                  if (isFilterChangingRef.current) return;
                  
                  playClickSound('click');
                  // Устанавливаем флаг ДО смены фильтра
                  isFilterChangingRef.current = true;
                  // Останавливаем воспроизведение перед сменой фильтра
                  if (isPlaying) {
                    onPlayPause(false);
                  }
                  if (audioRef.current) {
                    if (!audioRef.current.paused) {
                      audioRef.current.pause();
                    }
                    // Сбрасываем позицию воспроизведения на начало незаметно для пользователя
                    audioRef.current.currentTime = 0;
                    setCurrentTime(0);
                  }
                  setFilterMode('commercial');
                  
                  // Сбрасываем флаг после небольшой задержки
                  setTimeout(() => {
                    isFilterChangingRef.current = false;
                  }, 300);
                }}
                title="Show Commercial tracks only"
              >
                COMMERCIAL
              </button>
              <button
                className={`filter-button ${filterMode === 'underground' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Предотвращаем множественные клики
                  if (isFilterChangingRef.current) return;
                  
                  playClickSound('click');
                  // Устанавливаем флаг ДО смены фильтра
                  isFilterChangingRef.current = true;
                  // Останавливаем воспроизведение перед сменой фильтра
                  if (isPlaying) {
                    onPlayPause(false);
                  }
                  if (audioRef.current) {
                    if (!audioRef.current.paused) {
                      audioRef.current.pause();
                    }
                    // Сбрасываем позицию воспроизведения на начало незаметно для пользователя
                    audioRef.current.currentTime = 0;
                    setCurrentTime(0);
                  }
                  setFilterMode('underground');
                  
                  // Сбрасываем флаг после небольшой задержки
                  setTimeout(() => {
                    isFilterChangingRef.current = false;
                  }, 300);
                }}
                title="Show Underground tracks only"
              >
                UNDERGROUND
              </button>
            </div>
          </div>
        </div>

      {/* Спектрограмма трека */}
        <div className="boombox-progress-section">
      <div className="progress-bar-container">
        <Spectrogram 
          audioRef={audioRef}
          currentTime={currentTime}
          duration={duration}
          track={track}
          onSeek={(newTime) => {
            if (audioRef.current && duration) {
              audioRef.current.currentTime = newTime;
              setCurrentTime(newTime);
            }
          }}
        />
          </div>
          <div className="progress-time">
            {Math.floor(currentTime / 60)}:{(Math.floor(currentTime % 60)).toString().padStart(2, '0')} / {Math.floor(duration / 60)}:{(Math.floor(duration % 60)).toString().padStart(2, '0')}
        </div>
      </div>

      {/* Панель управления */}
        <div className="boombox-control-panel">
        <div className="control-labels">
          <span className="label-prev">PREV</span>
          <span className="label-play">PLAY</span>
          <span className="label-next">NEXT</span>
        </div>

        <div className="control-buttons-grid">
          <button 
            className="control-button previous-button" 
            onClick={handlePreviousTrack}
            title="Previous Track"
            disabled={!track || !tracks || tracks.length === 0}
          >
            <div className="button-triangle-left"></div>
          </button>
          <button 
            className={`control-button play-button ${isPlaying ? 'active' : ''}`}
            onClick={() => {
              if (!track) return;
              // Сбрасываем флаг остановки при нажатии Play
              if (!isPlaying) {
                isStoppedRef.current = false;
              }
              playClickSound('play');
              onPlayPause(!isPlaying);
            }}
            title={isPlaying ? "Pause" : "Play"}
            disabled={!track}
          >
            {isPlaying ? (
              <div className="button-lines">
                <div className="line"></div>
                <div className="line"></div>
              </div>
            ) : (
              <div className="button-triangle"></div>
            )}
          </button>
          <button 
            className="control-button next-button"
            onClick={handleNextTrack}
            title="Next Track"
            disabled={!track || !tracks || tracks.length === 0}
          >
            <div className="button-triangle-right"></div>
          </button>
          <button 
            className={`control-button rewind-button ${isRewinding ? 'active-rewind' : ''}`}
            onMouseDown={startRewind}
            onMouseUp={stopRewind}
            onMouseLeave={stopRewind}
            onTouchStart={(e) => {
              // Не вызываем preventDefault в пассивном обработчике
              startRewind();
            }}
            onTouchEnd={(e) => {
              // Не вызываем preventDefault в пассивном обработчике
              stopRewind();
            }}
            onClick={(e) => {
              e.preventDefault();
              handleRewindClick();
            }}
            title="Hold to rewind continuously, click for -5 seconds"
            disabled={!track}
          >
            <div className="button-triangles-left">
              <div className="triangle"></div>
              <div className="triangle"></div>
            </div>
            {isRewinding && <div className="rewind-indicator">⏪</div>}
          </button>
          <button 
            className="control-button stop-button" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStop();
            }} 
            title="Stop"
            type="button"
          >
            <div className="button-square"></div>
          </button>
          <button 
            className={`control-button fastforward-button ${isFastForwarding ? 'active-fastforward' : ''}`}
            onMouseDown={startFastForward}
            onMouseUp={stopFastForward}
            onMouseLeave={stopFastForward}
            onTouchStart={(e) => {
              // Не вызываем preventDefault в пассивном обработчике
              startFastForward();
            }}
            onTouchEnd={(e) => {
              // Не вызываем preventDefault в пассивном обработчике
              stopFastForward();
            }}
            onClick={(e) => {
              e.preventDefault();
              handleFastForwardClick();
            }}
            title="Hold to fast forward continuously, click for +5 seconds"
            disabled={!track}
          >
            <div className="button-triangles-right">
              <div className="triangle"></div>
              <div className="triangle"></div>
            </div>
            {isFastForwarding && <div className="fastforward-indicator">⏩</div>}
          </button>
        </div>
        
        <div className="control-labels-bottom">
          <span className="label-rew">REW</span>
          <span className="label-stop">STOP</span>
          <span className="label-ff">F.F.</span>
        </div>
      </div>

        {/* Треклист */}
        <div className="boombox-playlist-section">
          <div className="playlist-header">
            <div className="playlist-title-90s">TRACK LIST</div>
            <div className="playlist-count">{displayTracks.length} TRACKS</div>
          </div>
        <div className="playlist-box">
          {displayTracks.length > 0 ? (
            displayTracks.map((t, index) => (
              <div
                key={t.id || index}
                className={`playlist-item ${track && track.id === t.id ? 'active' : ''}`}
                onClick={() => {
                  console.log('Playlist item clicked:', t);
                  // КРИТИЧНО: Проверяем, что трек соответствует текущему фильтру
                  if (!t.project || t.project !== filterMode) {
                    console.warn('Track does not match current filter mode', { trackProject: t.project, filterMode });
                    return;
                  }
                  // Сбрасываем флаг остановки при выборе трека из списка
                  isStoppedRef.current = false;
                  if (onTrackSelect) {
                    onTrackSelect(t);
                  }
                }}
                title={`Click to play: ${t.artist} - ${t.title}`}
              >
                {t.artist ? t.artist.toUpperCase() : 'UNKNOWN'} - {t.title ? t.title.toUpperCase() : 'UNTITLED'}
              </div>
            ))
          ) : (
            <div className="playlist-empty">NO TRACKS AVAILABLE</div>
          )}
          </div>
        </div>

        {/* Нижняя панель с информацией */}
        <div className="boombox-bottom-panel">
          <div className="boombox-info">DOLBY NR • AUTO REVERSE • MEGA BASS</div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
